import { useState, useRef } from "react";
import { Modal } from "react-bootstrap";
import * as XLSX from "xlsx";
import Api from "../../Auth/Api";

// Cabeçalhos do template (na ordem em que aparecem na planilha).
// A senha NÃO entra: é sempre a própria matrícula.
const TEMPLATE_HEADERS = [
    "Nome Completo",
    "Matrícula",
    "CPF",
    "E-mail",
    "Unidade",
    "Cargo",
    "Nível",
];

// Linha de exemplo para orientar o preenchimento.
const TEMPLATE_EXEMPLO = {
    "Nome Completo": "JOÃO DA SILVA",
    "Matrícula": "12345",
    "CPF": "000.000.000-00",
    "E-mail": "joao@empresa.com",
    "Unidade": "MATRIZ",
    "Cargo": "OPERADOR",
    "Nível": "Aluno",
};

// Normaliza um cabeçalho (minúsculas, sem acento) para mapear colunas com flexibilidade.
const normalizar = (s) =>
    String(s ?? "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "");

// Mapa de cabeçalho normalizado -> campo enviado à API.
const MAPA_COLUNAS = {
    "nome completo": "nome_completo",
    "nome": "nome_completo",
    "matricula": "codigo",
    "codigo": "codigo",
    "cpf": "cpf",
    "e-mail": "email",
    "email": "email",
    "unidade": "unidade",
    "cargo": "cargo",
    "nivel": "nivel",
};

function ModalImportarUsuarios({ show, onHide, token, onToast, onSuccess }) {
    const [arquivoNome, setArquivoNome] = useState("");
    const [linhas, setLinhas] = useState([]);
    const [importando, setImportando] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [erroParse, setErroParse] = useState("");
    const inputRef = useRef(null);

    const resetar = () => {
        setArquivoNome("");
        setLinhas([]);
        setImportando(false);
        setResultado(null);
        setErroParse("");
        if (inputRef.current) inputRef.current.value = "";
    };

    const fechar = () => {
        resetar();
        onHide();
    };

    const baixarTemplate = () => {
        const ws = XLSX.utils.json_to_sheet([TEMPLATE_EXEMPLO], { header: TEMPLATE_HEADERS });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Usuarios");
        XLSX.writeFile(wb, "template_importacao_usuarios.xlsx");
    };

    const handleArquivo = (e) => {
        const file = e.target.files?.[0];
        setResultado(null);
        setErroParse("");
        setLinhas([]);
        if (!file) {
            setArquivoNome("");
            return;
        }
        setArquivoNome(file.name);

        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const wb = XLSX.read(ev.target.result, { type: "array" });
                const ws = wb.Sheets[wb.SheetNames[0]];
                // defval garante colunas vazias; cada item traz a chave do cabeçalho original.
                const json = XLSX.utils.sheet_to_json(ws, { defval: "" });

                const mapeadas = json
                    .map((row, i) => {
                        const out = { _linha: i + 2 }; // +2: linha 1 = cabeçalho.
                        Object.keys(row).forEach((col) => {
                            const campo = MAPA_COLUNAS[normalizar(col)];
                            if (campo) out[campo] = String(row[col] ?? "").trim();
                        });
                        return out;
                    })
                    // Ignora linhas totalmente vazias (sem nome e sem matrícula).
                    .filter((r) => (r.nome_completo || "") !== "" || (r.codigo || "") !== "");

                if (mapeadas.length === 0) {
                    setErroParse("Nenhuma linha preenchida encontrada. Use o template e preencha ao menos uma linha.");
                    return;
                }
                setLinhas(mapeadas);
            } catch (err) {
                setErroParse("Não foi possível ler o arquivo. Confira se é um .xlsx válido.");
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const importar = async () => {
        if (linhas.length === 0) return;
        setImportando(true);
        setResultado(null);
        try {
            const response = await Api.post(
                "api/usuarios/importar",
                { usuarios: linhas },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setResultado(response.data);
            const criados = response.data.criados || 0;
            const atualizados = response.data.atualizados || 0;
            if (criados > 0 || atualizados > 0) {
                onToast?.("Sucesso!", `${criados} criado(s) e ${atualizados} atualizado(s).`, "success");
                onSuccess?.();
            } else {
                onToast?.("Atenção", "Nenhum usuário foi importado. Verifique os erros.", "warning");
            }
        } catch (error) {
            onToast?.("Erro!", error.response?.data?.message || "Falha ao importar usuários.", "error");
        } finally {
            setImportando(false);
        }
    };

    return (
        <Modal size="lg" show={show} onHide={fechar}>
            <Modal.Header closeButton>
                <span className="fw-semibold fs-5 text-primary">
                    <i className="bi bi-upload me-2"></i>Importar usuários
                </span>
            </Modal.Header>
            <Modal.Body className="bg-gray-100 p-4">
                <p className="text-muted small mb-2">
                    Obrigatórios: nome, matrícula, unidade e cargo. Unidade e cargo precisam já estar cadastrados.
                    Nível vazio = Aluno. A matrícula é a chave (existe → atualiza; novo → senha igual à matrícula).
                </p>
                <p className="mb-4">
                    <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={baixarTemplate}>
                        <i className="bi bi-download me-1"></i>Baixar modelo (.xlsx)
                    </button>
                </p>

                <div className="card border-0 shadow-sm">
                    <div className="card-body">
                        {/* Input nativo fica oculto (regra global esconde input[type=file]); botão dispara o seletor. */}
                        <input
                            ref={inputRef}
                            type="file"
                            accept=".xlsx,.xls"
                            style={{ display: "none" }}
                            onChange={handleArquivo}
                        />
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => inputRef.current?.click()}
                        >
                            <i className="bi bi-paperclip me-2"></i>
                            {arquivoNome ? "Trocar arquivo" : "Selecionar arquivo (.xlsx)"}
                        </button>
                        {arquivoNome && !erroParse && linhas.length > 0 && (
                            <div className="alert alert-info mt-3 mb-0 py-2">
                                <i className="bi bi-file-earmark-spreadsheet me-2"></i>
                                <strong>{arquivoNome}</strong> — {linhas.length} linha(s) pronta(s) para importar.
                            </div>
                        )}
                        {erroParse && (
                            <div className="alert alert-danger mt-3 mb-0 py-2">
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>{erroParse}
                            </div>
                        )}
                    </div>
                </div>

                {resultado && (
                    <div className="card border-0 shadow-sm mt-3">
                        <div className="card-body">
                            <h6 className="fw-semibold mb-2"><i className="bi bi-clipboard-check me-2"></i>Resultado</h6>
                            <p className="mb-2">
                                <span className="badge bg-success me-2">{resultado.criados} criado(s)</span>
                                <span className="badge bg-primary me-2">{resultado.atualizados || 0} atualizado(s)</span>
                                <span className="badge bg-danger me-2">{resultado.erros?.length || 0} com erro</span>
                                <span className="text-muted small">de {resultado.total} linha(s)</span>
                            </p>
                            {resultado.erros?.length > 0 && (
                                <div className="table-responsive" style={{ maxHeight: "240px", overflowY: "auto" }}>
                                    <table className="table table-sm table-striped mb-0">
                                        <thead>
                                            <tr>
                                                <th>Linha</th>
                                                <th>Matrícula</th>
                                                <th>Erro</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {resultado.erros.map((err, i) => (
                                                <tr key={i}>
                                                    <td>{err.linha}</td>
                                                    <td>{err.codigo || "—"}</td>
                                                    <td className="text-danger small">{err.mensagem}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="justify-content-end">
                <button type="button" className="btn me-2" onClick={fechar} disabled={importando}>
                    Fechar
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={importar}
                    disabled={importando || linhas.length === 0}
                >
                    {importando ? (
                        <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Importando...</>
                    ) : (
                        <><i className="bi bi-upload me-2"></i>Importar {linhas.length > 0 ? `(${linhas.length})` : ""}</>
                    )}
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default ModalImportarUsuarios;
